import uid from 'easy-uid';
export { default as uuid } from 'easy-uid';

function arraysEqual(a, b) {
  if (a === b) {
    return true;
  }

  if (a == null || b == null || a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) {
      return false;
    }
  }

  return true;
}

class Circle {
  constructor({
    listen,
    unlisten,
    filter,
    execute
  } = {}) {
    this.id = uid();

    this._listen = listen || (() => {});

    this._unlisten = unlisten || (() => {});

    this._filter = filter || this._defaultFilter;

    this._execute = execute || ((...args) => this._defaultExecute(...args));

    this._entries = new Map();
    this._lockCircle = false;
    this._observers = [];
    this._notifyWithContext = this.notify.bind(this);
  }

  subscribe(listener) {
    this._observers.push(listener);
  }

  notify(...args) {
    this._observers.map(listener => {
      listener(...args);
    });
  }

  getEntries() {
    return this._entries;
  }

  register({
    read,
    write,
    meta = {
      interval: 180
    }
  }) {
    let id = uid();

    this._entries.set(id, {
      read,
      write,
      meta,
      time: Date.now() - meta.interval - 1,
      args: []
    });

    if (this._entries.size === 1) {
      this._listen(this._notifyWithContext);
    }

    return id;
  }

  unregister(id) {
    this._entries.delete(id);

    if (this._entries.size === 0) {
      this._unlisten(this._notifyWithContext);
    }
  }

  filter(circleEntries, ...args) {
    return this._filter(circleEntries, ...args);
  }

  read(circleEntries, ...args) {
    const now = Date.now();
    return circleEntries.map(entry => {
      entry.time = now;
      entry.args = args;
      let payload = typeof entry.read === 'function' ? entry.read(...args) : null;
      return Object.assign({
        payload
      }, entry);
    });
  }

  write(circleEntries, ...args) {
    return circleEntries.map(entry => {
      if (typeof entry.write === 'function') {
        return entry.write(entry, ...args);
      }
    });
  }

  *execute(...args) {
    if (this._lockCircle || this._entries.size === 0) {
      return [];
    }

    this._lockCircle = true;
    let modifiedEntries = yield* this._execute(...args);
    this._lockCircle = false;
    const originalEntries = Array.from(this.getEntries().values());
    const isCallAllEntriesWithArgs = originalEntries.filter(entry => arraysEqual(entry.args, args)).length === originalEntries.length;

    if (!isCallAllEntriesWithArgs) {
      this.notify(...args);
    }

    return modifiedEntries;
  }

  _defaultFilter(circleEntries) {
    const now = Date.now();
    return Array.from(circleEntries.values()).filter(loopEntry => {
      return loopEntry.time + loopEntry.meta.interval <= now;
    });
  }

  *_defaultExecute(...args) {
    let circleEntries = this.getEntries();
    circleEntries = this.filter(circleEntries, ...args);
    circleEntries = (yield circleEntries) || circleEntries;
    circleEntries = this.read(circleEntries, ...args);
    circleEntries = (yield circleEntries) || circleEntries;
    circleEntries = this.write(circleEntries, ...args);
    return circleEntries;
  }

}

class Infinite {
  constructor() {
    this._circles = new Map();
    this._scheduledCircles = new Map();
    this._executionInterval = 30;
    this._nextExecutionTime = 0;
    this._isSuspendedExecution = false;
    this._suspendExecutionWithContext = this._suspendExecution.bind(this);
  }

  add(circle) {
    this._circles.set(circle.id, circle);

    circle.subscribe((...rest) => this._onChange(circle, ...rest));
  }

  remove(circle) {
    this._circles.delete(circle.id);
  }

  _onChange(circle, ...args) {
    this._scheduledCircles.set(circle.id, {
      circle,
      args
    });

    if (!this._nextExecutionTime) {
      this._nextExecutionTime = Date.now() + this._executionInterval;
    }

    if (!this._isSuspendedExecution) {
      this._suspendExecution();
    }
  }

  _execute(circles) {
    const iterators = circles.map(({
      circle,
      args
    }) => circle.execute(...args));
    let isNextStep = true;

    while (isNextStep) {
      isNextStep = !iterators.reduce((end, iterator) => end && iterator.next().done, true);
    }
  }

  _suspendExecution() {
    if (this._nextExecutionTime <= Date.now()) {
      this._nextExecutionTime = 0;

      let copyOfScheduledCircles = this._scheduledCircles.values();

      this._scheduledCircles = new Map();
      this._isSuspendedExecution = false;

      this._execute(Array.from(copyOfScheduledCircles));
    } else {
      this._isSuspendedExecution = true;

      if (this._requestAnimationFrame()) {
        this._requestAnimationFrame()(this._suspendExecutionWithContext);
      } else {
        setTimeout(this._suspendExecutionWithContext, 1000 / 60);
      }
    }
  }

  _requestAnimationFrame() {
    return typeof window !== undefined && window.requestAnimationFrame;
  }

}

export { Circle, Infinite };
